import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare } from 'lucide-react';
import { brand, text, border, status, radius, typography, tint } from '../design-system/tokens';
import api from '../../services/api';
import KIQMarkdownLite from './KIQMarkdownLite';
import KIQLogoAnimado from '../design-system/KIQLogoAnimado';
import AvisoIANoDisponible from '../ia/AvisoIANoDisponible';
import { useCapacidadesIA, iaNoDisponible } from '../../hooks/useCapacidadesIA';

// KIQ Assistant — Fase 5 — Ask KIQ, conectado al backend real
// (POST /api/kiq/chat, backend/services/kiq/conversationService.js). Ya no
// hay una respuesta local fija (la placeholder honesta de Fase 3
// desapareció junto con la restricción que la exigía) — cada envío es un
// request real, cuya respuesta viene siempre del motor de KIQ (grounded en
// datos reales de GastroKIQ, nunca inventada aquí en el frontend).
//
// Reglas que este componente SÍ sigue conservando de Fase 3/3.1: nunca
// fabrica una respuesta por su cuenta (ni de éxito ni de error), nunca
// finge streaming, y el fallo de red se muestra con un mensaje honesto —
// jamás "todo parece estar bien" ni un dato inventado (sección 17 del
// pedido de Fase 5).
//
// Fase 5.1 — endurecimiento tras QA real en producción (Paso 3/9): las
// respuestas de KIQ (nunca las del usuario ni los mensajes de error, que
// son texto plano nuestro) se renderizan con KIQMarkdownLite — el Markdown
// crudo ("##", "**", "---") deja de mostrarse literal. Paso 10: la
// conversación se desplaza automáticamente al mensaje más nuevo dentro de
// SU PROPIO contenedor (el panel de KIQ, ya scrolleable — ver KIQPanel.js),
// nunca la página completa, y nunca robando el foco del usuario.
const CHIPS = ['Ventas', 'Costos', 'Rentabilidad', 'Inventario', 'Menú'];
const MENSAJE_ERROR = 'KIQ no pudo responder en este momento. Intenta nuevamente.';
// Mismo límite que el backend (conversationService.js:KIQ_MAX_TURNOS_HISTORIAL)
// — acotar también del lado del cliente evita mandar un payload cada vez
// más grande a medida que la conversación crece (el backend lo acotaría
// igual, pero no hay razón para depender solo de esa red de seguridad).
const MAX_TURNOS_ENVIADOS = 12;

let contadorId = 0;
function idUnico() {
  contadorId += 1;
  return `kiq-msg-${contadorId}`;
}

// Solo turnos reales usuario/kiq viajan como historial — un mensaje de
// error local (autor:'error') nunca es parte de la conversación real con
// el modelo, y reenviarlo no aportaría nada (el backend ya sabe que su
// propia respuesta anterior, si la hubo, fue exitosa).
function historialParaEnviar(listaMensajes) {
  return listaMensajes
    .filter((m) => m.autor === 'usuario' || m.autor === 'kiq')
    .slice(-MAX_TURNOS_ENVIADOS)
    .map((m) => ({ role: m.autor === 'usuario' ? 'user' : 'assistant', content: m.texto }));
}

export default function KIQComposer({ anio }) {
  const [mensajes, setMensajes] = useState([]);
  const [valor, setValor] = useState('');
  const [enfocado, setEnfocado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const finDeLaConversacionRef = useRef(null);
  // Beta — si el servidor confirma que el chat está apagado para esta sesión, no se
  // ofrece una caja de texto que terminaría en error (ver hooks/useCapacidadesIA.js).
  const chatApagado = iaNoDisponible(useCapacidadesIA(), 'kiq_chat');

  // Paso 10 — nunca el usuario debe buscar manualmente el mensaje más
  // nuevo. `scrollIntoView` desplaza el ANCESTRO scrolleable más cercano
  // (el cuerpo del panel de KIQ, ver KIQPanel.js) — nunca `window`/el body
  // completo de la app, que además ya está bloqueado mientras el panel está
  // abierto (Layout.js). Guardado con optional chaining: en el entorno de
  // pruebas (jsdom) scrollIntoView puede no existir y nunca debe crashear.
  useEffect(() => {
    finDeLaConversacionRef.current?.scrollIntoView?.({ block: 'end' });
  }, [mensajes.length, enviando]);

  function manejarChip(tema) {
    setValor(tema);
  }

  async function manejarEnviar(e) {
    e.preventDefault();
    const texto = valor.trim();
    // `enviando` evita un doble envío accidental (doble click/Enter) mientras
    // la respuesta anterior sigue en curso — nunca se dispara una segunda
    // request en paralelo para la misma conversación.
    if (!texto || enviando) return;

    const historial = historialParaEnviar(mensajes);
    setMensajes((prev) => [...prev, { id: idUnico(), autor: 'usuario', texto }]);
    setValor('');
    setEnviando(true);

    try {
      const { data } = await api.post('/api/kiq/chat', { message: texto, year: anio, conversation: historial });
      const respuesta = typeof data?.answer === 'string' && data.answer.trim() ? data.answer.trim() : MENSAJE_ERROR;
      setMensajes((prev) => [...prev, { id: idUnico(), autor: 'kiq', texto: respuesta }]);
    } catch (err) {
      // Degradación honesta: nunca se fabrica una respuesta ni se muestra un
      // mensaje de "todo está bien" — un error local, claramente marcado, que
      // tampoco se reenvía como historial real (ver historialParaEnviar).
      // Fase 6.3D.10C — un límite o una pausa del gobierno de IA (code IA_*)
      // trae un mensaje neutro del servidor ("límite diario", "no disponible"):
      // se muestra tal cual; cualquier otro fallo, el mensaje genérico.
      const datos = err?.response?.data;
      const aviso = typeof datos?.code === 'string' && datos.code.startsWith('IA_') && typeof datos.error === 'string' && datos.error.trim()
        ? datos.error.trim() : MENSAJE_ERROR;
      setMensajes((prev) => [...prev, { id: idUnico(), autor: 'error', texto: aviso }]);
    } finally {
      setEnviando(false);
    }
  }

  if (chatApagado) {
    return (
      <section style={{ display: 'flex', flexDirection: 'column', gap: '8px', border: `1px solid ${border.default}`, borderRadius: radius.lg, padding: '12px', background: 'var(--card-alt)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <MessageSquare size={14} color={text.muted} />
          <h2 style={{ ...typography.title, color: text.primary, margin: 0 }}>Pregúntale a KIQ</h2>
        </div>
        <AvisoIANoDisponible>El chat de KIQ no está disponible por ahora. Las recomendaciones de KIQ siguen funcionando.</AvisoIANoDisponible>
      </section>
    );
  }

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '8px', border: `1px solid ${border.default}`, borderRadius: radius.lg, padding: '12px', background: 'var(--card-alt)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        <MessageSquare size={14} color={text.muted} />
        <h2 style={{ ...typography.title, color: text.primary, margin: 0 }}>Pregúntale a KIQ</h2>
      </div>

      {mensajes.length > 0 && (
        <div role="log" aria-label="Conversación con KIQ" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {mensajes.map((m) => (
            <div
              key={m.id}
              style={{
                alignSelf: m.autor === 'usuario' ? 'flex-end' : 'flex-start',
                maxWidth: m.autor === 'usuario' ? '85%' : '92%',
                padding: '9px 12px',
                borderRadius: radius.md,
                fontSize: '13px',
                lineHeight: 1.5,
                background: m.autor === 'usuario' ? 'var(--card)' : m.autor === 'error' ? tint(status.danger, 0.12) : 'var(--card-hover)',
                border: `1px solid ${m.autor === 'error' ? status.danger : border.default}`,
                color: text.secondary,
              }}
            >
              {/* Solo las respuestas de KIQ pasan por el parser de Markdown
                  acotado (Paso 3) — el mensaje del usuario y los errores
                  locales son siempre texto plano nuestro, nunca del modelo,
                  así que nunca necesitan interpretarse. */}
              {m.autor === 'kiq' ? <KIQMarkdownLite texto={m.texto} /> : m.texto}
            </div>
          ))}
          {enviando && (
            <div aria-live="polite" style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '8px', padding: '2px 4px' }}>
              {/* KIQ pensando (design_handoff_carga) — variante "orbita" de
                  KIQLogoAnimado reemplaza los 3 puntos kiqdot; puramente
                  decorativo, el texto sigue siendo la fuente real del estado
                  para lectores de pantalla (aria-live ya lo anuncia). El
                  README pide el texto en "--ink3", que no existe en el
                  sistema real de tokens — se usa text.muted, el mismo token
                  que ya usa este archivo para texto secundario. */}
              <KIQLogoAnimado variante="orbita" size={32} />
              <span style={{ fontSize: '13px', color: text.muted }}>KIQ está pensando…</span>
            </div>
          )}
          <div ref={finDeLaConversacionRef} />
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {CHIPS.map((tema) => (
          <button
            key={tema}
            type="button"
            onClick={() => manejarChip(tema)}
            className="kiq-focusable"
            style={{
              padding: '5px 10px', borderRadius: radius.pill, border: `1px solid ${border.default}`,
              background: 'var(--card)', color: text.secondary, fontSize: '11.5px', fontWeight: 600, cursor: 'pointer',
            }}
          >
            {tema}
          </button>
        ))}
      </div>

      <form onSubmit={manejarEnviar} style={{ display: 'flex', gap: '8px' }}>
        <label htmlFor="kiq-composer-input" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
          Pregúntale a KIQ sobre tu restaurante
        </label>
        <input
          id="kiq-composer-input"
          type="text"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          onFocus={() => setEnfocado(true)}
          onBlur={() => setEnfocado(false)}
          disabled={enviando}
          placeholder="Pregúntale a KIQ sobre tu restaurante…"
          style={{
            flex: 1, height: '38px', padding: '0 12px', borderRadius: radius.md,
            border: `1px solid ${enfocado ? brand.kiqBlue : border.default}`,
            boxShadow: enfocado ? `0 0 0 3px ${tint(brand.kiqBlue, 0.18)}` : 'none',
            background: 'var(--card)', color: text.primary, fontSize: '13.5px', outline: 'none',
            transition: 'border-color 140ms ease, box-shadow 140ms ease',
            opacity: enviando ? 0.7 : 1,
          }}
        />
        <button
          type="submit"
          aria-label="Enviar"
          disabled={!valor.trim() || enviando}
          className="kiq-focusable"
          style={{
            width: '38px', height: '38px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: radius.md, border: 'none', background: brand.gradient, color: text.inverse,
            cursor: valor.trim() && !enviando ? 'pointer' : 'not-allowed', opacity: valor.trim() && !enviando ? 1 : 0.5,
          }}
        >
          <Send size={15} />
        </button>
      </form>
    </section>
  );
}
